import { Button } from '@/shadcn-ui/button';
import { cn } from '@/shadcn-ui/utils';
import React, { forwardRef } from 'react';

export interface FilterPillProps extends React.ComponentPropsWithoutRef<'button'> {
  icon?: React.ReactNode;
  text: string | React.ReactNode;
  prefix?: string;
  variant?: 'default' | 'active';
  showShorthandCopy?: boolean;
  dark?: boolean;
}

const FilterPill = forwardRef<HTMLButtonElement, FilterPillProps>(
  (
    {
      icon,
      variant = 'default',
      dark = true,
      prefix,
      text,
      className,
      showShorthandCopy = true,
      ...props
    }: FilterPillProps,
    forwardedRef
  ) => (
    <Button
      size="none"
      variant="clear"
      ref={forwardedRef}
      className={cn(
        'group inline-flex h-auto w-fit max-w-full flex-shrink items-center justify-center gap-2 whitespace-nowrap rounded-[5rem] border border-flexcar-neutral100/25 bg-flexcar-neutral100/10 px-4 py-2 text-sm font-medium shadow-md ring-offset-background transition-all hover:border-flexcar-neutral100/35 hover:bg-flexcar-neutral100/20 hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50',
        {
          'border-flexcar-neutral100 bg-flexcar-neutral100 hover:bg-flexcar-neutral100/90':
            dark && variant === 'active',
          'bg-flexcar-neutral90 shadow-none hover:bg-flexcar-neutral80 focus-visible:ring-flexcar-neutral50':
            !dark,
        },
        className
      )}
      {...props}
    >
      {icon ? <div className="flex-shrink-0">{icon}</div> : null}
      <p
        className={cn(
          'body2-strong flex flex-shrink items-center gap-[6px] overflow-hidden text-ellipsis whitespace-nowrap text-flexcar-neutral100 transition-all',
          {
            'text-flexcar-neutral10': dark && variant === 'active',
            'text-flexcar-neutral1': !dark,
          }
        )}
      >
        {prefix ? (
          <span className={cn({ 'hidden sm:inline': showShorthandCopy })}>
            {prefix}:{' '}
          </span>
        ) : null}
        {text}
      </p>
    </Button>
  )
);

FilterPill.displayName = 'FilterPill';

export default FilterPill;
